import { trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { Subject, switchMap } from 'rxjs';
import { maxHeightIfWithMarginTop } from '../../../animations/max-height.if.animation';
import { settingsMainPage, settingsSubPage } from '../../../animations/settings-page.animation';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { OnGetData } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { AnnotationsSettingItemComponent } from '../../components/annotations-setting-item/annotations-setting-item.component';
import { AssetDownloadSettingItemComponent } from '../../components/asset-download-setting-item/asset-download-setting-item.component';
import { AssetStatusSettingCustomizeComponent } from '../../components/asset-status-setting-customize/asset-status-setting-customize.component';
import { AssetStatusSettingItemComponent } from '../../components/asset-status-setting-item/asset-status-setting-item.component';
import { AssetVersionsSettingItemComponent } from '../../components/asset-versions-setting-item/asset-versions-setting-item.component';
import { ColorLabelsSettingCustomizeComponent } from '../../components/color-labels-setting-customize/color-labels-setting-customize.component';
import { ColorLabelsSettingItemComponent } from '../../components/color-labels-setting-item/color-labels-setting-item.component';
import { CommentsSettingItemComponent } from '../../components/comments-setting-item/comments-setting-item.component';
import { DownloadSizesSettingCustomizeComponent } from '../../components/download-sizes-setting-customize/download-sizes-setting-customize.component';
import { DownloadSizesSettingItemComponent } from '../../components/download-sizes-setting-item/download-sizes-setting-item.component';
import { FavouritesSettingItemComponent } from '../../components/favorites-setting-item/favorites-setting-item.component';
import { GalleryDownloadApprovedOnlySettingItemComponent } from '../../components/gallery-download-approved-only-setting-item/gallery-download-approved-only-setting-item.component';
import { GalleryDownloadSettingItemComponent } from '../../components/gallery-download-setting-item/gallery-download-setting-item.component';
import { GalleryMetadataSettingItemComponent } from '../../components/gallery-metadata-setting-item/gallery-metadata-setting-item.component';
import { GallerySettingsGroupComponent } from '../../components/gallery-settings-group/gallery-settings-group.component';
import { GallerySettingsPresetsDropdownComponent } from '../../components/gallery-settings-presets-dropdown/gallery-settings-presets-dropdown.component';
import { GalleryStatusSettingItemComponent } from '../../components/gallery-status-setting-item/gallery-status-setting-item.component';
import { GalleryWatermarkSettingCustomizeComponent } from '../../components/gallery-watermark-setting-customize/gallery-watermark-setting-customize.component';
import { GalleryWatermarkSettingItemComponent } from '../../components/gallery-watermark-setting-item/gallery-watermark-setting-item.component';
import { GalleryWorkflowSettingItemComponent } from '../../components/gallery-workflow-setting-item/gallery-workflow-setting-item.component';

@Component({
  selector: 'app-new-gallery-settings-menu',
  imports: [
    GalleryStatusSettingItemComponent,
    AssetStatusSettingItemComponent,
    AssetStatusSettingCustomizeComponent,
    FavouritesSettingItemComponent,
    CommentsSettingItemComponent,
    AnnotationsSettingItemComponent,
    ColorLabelsSettingItemComponent,
    ColorLabelsSettingCustomizeComponent,
    GallerySettingsGroupComponent,
    GalleryDownloadSettingItemComponent,
    AssetDownloadSettingItemComponent,
    DownloadSizesSettingItemComponent,
    DownloadSizesSettingCustomizeComponent,
    GalleryMetadataSettingItemComponent,
    AssetVersionsSettingItemComponent,
    GalleryWatermarkSettingItemComponent,
    GalleryWatermarkSettingCustomizeComponent,
    GalleryWorkflowSettingItemComponent,
    GallerySettingsPresetsDropdownComponent,
    GalleryDownloadApprovedOnlySettingItemComponent,
    RouterLink,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('mainPage', settingsMainPage),
    trigger('subPage', settingsSubPage),
    trigger('dynamicRow', maxHeightIfWithMarginTop(40, 0, -10)),
  ],
  templateUrl: './gallery-settings-menu.component.html',
  styleUrl: './gallery-settings-menu.component.scss',
})
export class GallerySettingsMenuComponent implements OnGetData {
  dialogName = 'gallery-settings-menu';
  private galleryService = inject(GalleryService);
  private galleryId = new Subject<string>();
  gallery = toSignal(this.galleryId.pipe(switchMap(galleryId => this.galleryService.getGallery(galleryId))));
  subPage = signal<'asset-status' | 'color-labels' | 'download-sizes' | 'gallery-watermark' | undefined>(undefined);

  dialogOptions(): DialogOptions {
    return { spacingX: 20, spacingY: 24, layout: 'side' };
  }

  onGetData(data: { gallery: GalleryDto }) {
    this.galleryId.next(data.gallery.id);
  }
}
